import axios from 'axios';
import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue';
import { computed, ref, toValue } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useToast } from '@rygen/rygen-ui/composables';
import { Loadable, Money } from '@rygen/rygen-ui/model';
import { ShipmentFinancials } from '@rygen/rygen-ui/model/shipmentFinancials';

import api from '@/api';
import { Shipment } from '@/model';

import { useSelectedDomain } from './useSelectedDomain';

const shipmentFinancialsCache: Ref<{
  [shipmentId: number]: ShipmentFinancials;
}> = ref({});

interface UseShipmentFinancialsResponse {
  /**
   * A delay can be specified to give Billing Module time to update asynchronously after a shipment is updated
   * @param delay number of ms before API call is actually executed
   */
  loadShipmentFinancials: (delay?: number) => Promise<void>;
  /**
   * Synchronously publishes an update to billing, and then returns the resulting shipment financials object.
   */
  resyncShipmentFinancials: () => Promise<void>;
  shipmentFinancials: ComputedRef<Loadable<ShipmentFinancials>>;
  isPayable: ComputedRef<boolean>;
  isActualCost: ComputedRef<boolean>;
  costEstimate: ComputedRef<Money | undefined>;
  actualCost: ComputedRef<Money | undefined>;
}

export const useShipmentFinancials = (
  shipment: MaybeRefOrGetter<Shipment>,
): UseShipmentFinancialsResponse => {
  const { t } = useI18n();
  const watchSelectedDomain = useSelectedDomain();
  const toast = useToast();

  const loading = ref(false);
  const error: Ref<unknown> = ref();

  const shipmentFinancials = computed(() => {
    const rawShipment = toValue(shipment);

    return {
      data: rawShipment.id
        ? shipmentFinancialsCache.value[rawShipment.id]
        : undefined,
      error: error.value,
      loading: loading.value,
    };
  });

  const isPayable = computed(
    () => shipmentFinancials.value.data?.payable ?? false,
  );

  const isActualCost = computed(
    () =>
      shipmentFinancials.value.data?.ledgerContainsOriginalInvoice() ?? false,
  );

  const actualCost = computed(() =>
    shipmentFinancials.value.data?.getActualCost(),
  );

  const costEstimate = computed(() =>
    shipmentFinancials.value.data?.getEstimatedCost(),
  );

  /**
   * A delay can be specified to give Billing Module time to update asynchronously after a shipment is updated
   * @param delay number of ms before API call is actually executed
   */
  const loadShipmentFinancials = async (delay = 0): Promise<void> => {
    if (
      !authorization.hasAuthority('shipmentFinancials', 'readShipmentLedger')
    ) {
      console.warn('User does not have authority to view Shipment Financials');
      return;
    }

    const rawShipment = toValue(shipment);
    if (!rawShipment.id) {
      throw new Error('Missing id for Shipment');
    }

    try {
      loading.value = true;
      error.value = undefined;

      if (delay > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        // ensure error is reset after the delay since it may have been set by a parallel call to this method
        error.value = undefined;
      }

      shipmentFinancialsCache.value[rawShipment.id] =
        await api.shipmentFinancials.getShipmentFinancialsById(rawShipment.id);
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      error.value = e as Error;
      console.error('Error fetching shipment financials', e);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Synchronously publishes an update to billing, and then returns the resulting shipment financials object.
   */
  const resyncShipmentFinancials = async (): Promise<void> => {
    if (
      !authorization.hasAuthority('shipmentFinancials', 'readShipmentLedger')
    ) {
      console.warn('User does not have authority to view Shipment Financials');
      return;
    }

    const rawShipment = toValue(shipment);
    if (!rawShipment.id) {
      throw new Error('Missing id for Shipment');
    }

    try {
      loading.value = true;
      error.value = undefined;

      shipmentFinancialsCache.value[rawShipment.id] =
        await api.shipmentFinancials.resyncShipmentFinancials(rawShipment.id);
      watchSelectedDomain(() => loadShipmentFinancials());
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      error.value = e as Error;
      console.error('Error refreshing shipment financials', e);
      toast.addError({
        detail: t('errors.refreshingParam', {
          param: t('shipments.financials.shipmentFinancials'),
        }),
        error: e,
      });
    } finally {
      loading.value = false;
    }
  };

  return {
    loadShipmentFinancials,
    resyncShipmentFinancials,
    shipmentFinancials,
    isPayable,
    isActualCost,
    actualCost,
    costEstimate,
  };
};
